import { Component, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { AnalyticService, NotifyService } from '@app/services';
import { TimeOffPolicy, TimeOffSeniorityOverridePayload } from '@app/models/time-off-v3/time-off-policy.model';
import {
    AmountAccruedPerYearUpdateCardComponent,
    PolicyChangeConfirmationDialogComponent,
} from '@time-off-v3/components';
import { BaseForm } from '@forms/base.form';
import differenceBy from 'lodash-es/differenceBy';

@Component({
    templateUrl: './accrual-update.template.html',
    styleUrls: ['./accrual-update.style.scss'],
})
export class AccrualUpdateView extends BaseForm implements OnInit {
    @ViewChild('confirmation', { static: true }) confirmation: PolicyChangeConfirmationDialogComponent;
    @ViewChild('amountAccruedPerYear') amountAccruedPerYearForm: AmountAccruedPerYearUpdateCardComponent;

    isLoading = false;
    timeOffPolicy: TimeOffPolicy;

    days = 0;

    constructor(
        private analyticService: AnalyticService,
        private notify: NotifyService,
        private route: ActivatedRoute,
        private location: Location,
        private router: Router
    ) {
        super();
    }

    ngOnInit(): void {
        this.timeOffPolicy = this.route.snapshot.data.models.get('policy');
    }

    onCancel(): void {
        this.location.back();
    }

    async onSave(): Promise<void> {
        this.submit();

        if (!this.valid) {
            return;
        }

        if (this.timeOffPolicy.isEssentialFieldDirty()) {
            const confirmation = await this.confirmation.open();

            if (!confirmation) {
                return;
            }
        }

        this.isLoading = true;

        try {
            if (!this.timeOffPolicy.isUnlimited) {
                this.timeOffPolicy.timeOffSeniorityOverridesPayload = this.getUpdatedSeniorityOverridesPayload();
            }

            await this.timeOffPolicy.save();

            this.analyticService.trackEvent(AnalyticEvents.TimeOffTimeOffPolicyEditTimeOffPolicy);
            this.notify.success('time-off-v3.update.savedSuccessfully');
            this.router.navigate(['/', 'time-off', 'v3', 'types-and-policies'], { relativeTo: this.route });
        } catch (error) {
            this.notify.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    private getUpdatedSeniorityOverridesPayload(): Array<TimeOffSeniorityOverridePayload> {
        // Check if we need to update, create or delete any seniority overrides and set it as a payload
        const overrides = this.amountAccruedPerYearForm?.timeOffSeniorityOverrides;
        const cacheOverrides = this.amountAccruedPerYearForm?.timeOffSeniorityOverridesCache;

        const toBeUpdatedOrCreatedSeniorityOverrides = overrides
            .filter((seniorityOverride) => seniorityOverride.isDirty())
            .map((seniorityOverride) => seniorityOverride.toPayload());

        const toBeDeleteSeniorityOverrides = differenceBy(cacheOverrides, overrides, 'id').map((seniorityOverride) =>
            seniorityOverride.toPayload({ delete: true })
        );

        return [...toBeDeleteSeniorityOverrides, ...toBeUpdatedOrCreatedSeniorityOverrides];
    }
}
